import { useContext, createContext, useState, useEffect, useRef } from "react";

import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";

import useLazyGetPolls from "@/view/utils/useLazyGetPolls";
import usePollingForPollResults from "@/view/utils/usePollingForPollResults";
import useHandlePollAfterAssignmentUpdate from "../useHandlePollAfterAssignmentUpdate";
import usePollResultsTracker from "../usePollResultsTracker/usePollResultsTracker";
import usePollState from "./utils/usePollState";

const propTypes = {
  currentAssignment: PropTypes.object,
  currentLevelLanguage: PropTypes.string,
  children: PropTypes.node,
  selectedAssignmentId: PropTypes.string,
  userProperties: PropTypes.shape({
    isStudent: PropTypes.bool,
    isTeacher: PropTypes.bool,
  }),
};

const PollContext = createContext();

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export const PollContextProvider = ({
  currentAssignment,
  currentLevelLanguage,
  children,
  selectedAssignmentId,
  userProperties,
}) => {
  const { ref: inViewRef, inView } = useInView();
  const [isUsingSocketConnection, setIsUsingSocketConnection] = useState(false);
  const webSocketRef = useRef(null);

  const [getPoll] = useLazyGetPolls();
  const {
    activePoll,
    assignedPoll,
    customPoll,
    isCreateMode,
    isDraftAssignment,
    isEditMode,
    isPollSelected,
    isValidateFormFieldsActive,
    pollTemplates,
    setActivePoll,
    setAssignedPoll,
    setCustomPoll,
    setIsCreateMode,
    setIsEditMode,
    setIsPollSelected,
    setIsValidateFormFieldsActive,
    totalPollAssignees,
  } = usePollState({
    currentAssignment,
    currentLevelLanguage,
    selectedAssignmentId,
    userProperties,
  });

  const { webSocketStatus } = usePollResultsTracker({
    assignedPoll,
    setAssignedPoll,
    webSocketRef,
    getPoll,
  });

  useEffect(() => {
    if (webSocketStatus === "CONNECTED" && !isUsingSocketConnection) {
      setIsUsingSocketConnection(true);
    } else {
      if (isUsingSocketConnection) {
        setIsUsingSocketConnection(false);
      }
    }
  }, [webSocketStatus]);

  usePollingForPollResults({
    canSeeResults: assignedPoll?.can_see_results,
    getPoll,
    isDraftAssignment,
    isInView: inView,
    pollId: assignedPoll?.id,
    setAssignedPoll,
    totalPollAssignees,
    userProperties,
    isUsingSocketConnection,
  });

  const [handleAssignmentPoll] = useHandlePollAfterAssignmentUpdate({
    activePoll,
    assignedPoll,
    customPoll,
    isEditMode,
    isPollSelected,
    setAssignedPoll,
    setIsEditMode,
  });

  return (
    <PollContext.Provider
      value={{
        activePoll,
        assignedPoll,
        handleAssignmentPoll,
        inViewRef,
        isCreateMode,
        isEditMode,
        isPollSelected,
        isValidateFormFieldsActive,
        pollTemplates,
        setActivePoll,
        setAssignedPoll,
        setCustomPoll,
        setIsCreateMode,
        setIsEditMode,
        setIsPollSelected,
        setIsValidateFormFieldsActive,
        totalPollAssignees,
      }}
    >
      {children}
    </PollContext.Provider>
  );
};

PollContextProvider.propTypes = propTypes;

const usePollContext = () => {
  const pollContext = useContext(PollContext);

  if (pollContext === undefined) {
    throw new Error("PollContext must be used within a PollContextProvider.");
  }

  return pollContext;
};

export default usePollContext;
